import * as Sentry from "@sentry/nextjs";
import { makeAutoObservable, runInAction } from "mobx";

import { hasSubRoles } from "@/(service)/survey-describe-detail/subRoles";
import { SurveyUseOption } from "@/(service)/survey-use/types";
import { applyOnboardingTemplates } from "@/api";
import { getApiErrorMessage } from "@/api/client";
import { REDIRECT_AFTER_LOGIN_HREF_LS_KEY, type signInProvider } from "@/models/auth/authTypes";
import { forgetPendingCourse } from "@/store/pendingCourse";
import { validateName, validateUserEmail, validateUsername } from "@/store/user/userUtils";
import { teamPageUrl } from "@/urls";
import { mergeSignInLinkBrowsers, signInLinkRequesterId } from "@/utils/posthog/signInLink";
import { setSignupVia } from "@/utils/posthog/via";

import type { ApplyOnboardingTemplatesBodyRolesItem } from "@/api";
import type { FormFieldModel } from "@/components/form/FormFieldModel";
import type { RootStore } from "@/store/RootStore";
import type { UserFields } from "@/store/user/userTypes";
import type { FormEvent } from "react";

const EMAIL_LS_KEY = "emailForSignIn";

/** Delay before clearing the redirect guard after navigation. router.push() returns void,
 * so we can't await navigation completion. This gives concurrent callers time to see the guard. */
const REDIRECT_GUARD_RESET_MS = 200;

export class SignInFlowStore implements FormFieldModel<UserFields> {
  rootStore: RootStore;

  /** Memoized promise for single-flight: concurrent callers share it, only one redirect runs. */
  private redirectAfterLoginPromise: Promise<void> | null = null;

  /** Selected avatar file before user is updated; uploaded after onUserNameSubmit(). */
  pendingAvatarFile: File | null = null;

  /** Object URL for preview; revoke when clearing. */
  pendingAvatarPreviewUrl: string | null = null;

  formFields: FormFieldModel<UserFields>["formFields"] = {
    email: {
      value: "",
      focused: false,
      error: null,
      validate: (value) => validateUserEmail(value),
    },
    userName: {
      value: "",
      focused: false,
      error: null,
      validate: (value) => validateUsername(value),
    },
    fullName: {
      value: "",
      focused: false,
      error: null,
      validate: (value) => validateName(value),
    },
  };

  constructor(rootStore: RootStore) {
    makeAutoObservable(this);

    this.rootStore = rootStore;
  }

  async handleRedirectResult() {
    try {
      // Skip redirect handling if we're already on a dailychallenge page
      // This prevents double-redirect after signup when user is redirected to dailychallenge
      // and handleRedirectResult runs again on the new page
      if (typeof window !== "undefined" && window.location.pathname.startsWith("/dailychallenge")) {
        return;
      }

      const authResult = await this.rootStore.auth.waitForAuthReady(async () => {
        return this.rootStore.auth.getRedirectResult();
      });
      if (!authResult || !authResult.user) {
        return;
      }
      await this.rootStore.auth.fetchGoogleAvatar(authResult);
      this.redirectToNextSignInStep();
    } catch (err) {
      this.rootStore.auth.setError(getApiErrorMessage(err));
      this.rootStore.auth.setStatus("error");
      Sentry.captureException(err);
    }
  }

  async signInWithProviderAndRedirect(providerName: signInProvider) {
    try {
      // Preserve existing dailychallenge redirect URL before starting OAuth
      // This ensures users coming from dailychallenge via /signup or /login
      // are still redirected back to dailychallenge after completing signup
      if (typeof window !== "undefined") {
        const existingRedirect = localStorage.getItem(REDIRECT_AFTER_LOGIN_HREF_LS_KEY);
        const hasDailyChallengeRedirect = existingRedirect?.includes("/dailychallenge");

        // If no dailychallenge redirect is stored, check if we're currently on dailychallenge
        // (handles case where user landed on signup/login from dailychallenge without prior redirect)
        if (!hasDailyChallengeRedirect && document.referrer?.includes("/dailychallenge")) {
          localStorage.setItem(REDIRECT_AFTER_LOGIN_HREF_LS_KEY, document.referrer);
        }
      }

      const result = await this.rootStore.auth.signInWithProvider(providerName);
      // if no result, don't redirect to next sign in step cause we triggered google redirect sign in
      if (!result) {
        return;
      }
      this.redirectToNextSignInStep();
    } catch (err) {
      this.rootStore.auth.setError(getApiErrorMessage(err));
      this.rootStore.auth.setStatus("error");
      Sentry.captureException(err);
      throw err;
    }
  }

  async signInWithLinkAndRedirect(email: string) {
    const requesterId = signInLinkRequesterId();
    try {
      await this.rootStore.auth.signInWithLink(email);
      // The Firebase uid becomes the PostHog distinct id. A new user has no currentUser
      // until onboarding creates the account, so it can't be used here.
      const uid = this.rootStore.auth.fbUser?.uid;
      if (requesterId && uid && !this.rootStore.auth.emailClaimed) {
        mergeSignInLinkBrowsers(requesterId, uid);
      }
      this.redirectToNextSignInStep();
    } catch (err) {
      this.rootStore.auth.setError(getApiErrorMessage(err));
      this.rootStore.auth.setStatus("error");
      Sentry.withScope((scope) => {
        scope.setExtras({ step: "sigin-with-link-redirect", email });
        scope.captureException(err);
      });
    }
  }

  async onResendEmailLinkClick(email: string): Promise<void> {
    try {
      await this.rootStore.auth.sendSignInLinkToEmail(email);
      runInAction(() => {
        this.savedEmail = email;
      });
    } catch (err) {
      this.rootStore.auth.setError(getApiErrorMessage(err));
      this.rootStore.auth.setStatus("error");
      Sentry.withScope((scope) => {
        scope.setExtras({ step: "resend-email-link", email });
        scope.captureException(err);
      });
      throw err;
    }
  }

  async onSignInLinkSubmit(fields: Record<UserFields, string>, e: FormEvent): Promise<void> {
    e.preventDefault();
    const { email } = fields;
    try {
      await this.rootStore.auth.sendSignInLinkToEmail(email);
      runInAction(() => {
        this.savedEmail = email;
      });
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setExtras({ step: "signin-submit-link", email });
        scope.captureException(err);
      });
      throw err;
    }
  }

  async onFullNameSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    this.redirectToAddUsername();
    return Promise.resolve();
  }

  async onUserNameSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    try {
      await this.rootStore.auth.updateAndFetchUserData({
        name: this.formFields.fullName.value,
        username: this.formFields.userName.value,
        googleAccessToken: this.rootStore.auth.oauthCredential?.accessToken,
      });
      setSignupVia();
      const fileToUpload = this.pendingAvatarFile;
      if (fileToUpload) {
        await this.rootStore.teams.fetchTeams();
        const { personalTeam } = this.rootStore.teams;
        if (personalTeam) {
          await personalTeam.uploadAvatar({ avatar: { file: fileToUpload } });
        }
      }
      // fetch user data again to get the updated user data (including google avatar)
      await this.rootStore.auth.fetchUserData({ force: true });
      const existingRedirect = localStorage.getItem(REDIRECT_AFTER_LOGIN_HREF_LS_KEY);
      if (!existingRedirect) {
        localStorage.setItem(REDIRECT_AFTER_LOGIN_HREF_LS_KEY, "/getting-started");
      }
      this.redirectToSurveyDescribe();
    } catch (err) {
      this.rootStore.auth.setError(getApiErrorMessage(err));
      this.rootStore.auth.setStatus("error");
      Sentry.captureException(err);
    }
  }

  async onSurveyDescribeSubmit(
    role: ApplyOnboardingTemplatesBodyRolesItem,
    isStudentFlowEnabled = false,
  ): Promise<void> {
    try {
      await applyOnboardingTemplates({ roles: [role] });
    } catch (err) {
      Sentry.captureException(err);
    }
    if (role === "student" && isStudentFlowEnabled) {
      this.redirectToSelectSchool();
      return;
    }
    if (hasSubRoles(role)) {
      this.rootStore?.router?.push(`/survey-describe-detail?role=${role}`);
    } else {
      this.redirectToSurveyUse();
    }
  }

  async onSurveyDescribeSkip(): Promise<void> {
    return this.onSurveyDescribeSubmit("other");
  }

  onSurveyDescribeDetailSubmit(): void {
    this.redirectToSurveyUse();
  }

  skipStudentOnboarding(): void {
    forgetPendingCourse();
    this.redirectToSurveyUse();
  }

  async onSurveyUseSubmit(value: string): Promise<void> {
    const existingRedirect = localStorage.getItem(REDIRECT_AFTER_LOGIN_HREF_LS_KEY);

    // If user was invited to corca by email he will visit /accept-invite page by invite link and it will be stored in localStorage
    // after he passes registration flow we redirect him to /accept-invite/[token] page so he can see "You were invited to join a team" message
    if (
      existingRedirect?.includes("/accept-invite") ||
      value === SurveyUseOption.Personal.toString()
    ) {
      this.redirectAfterLogin();
    } else {
      // Otherwise, if he picked "As a team" we redirect him to /create-team page to create a new team
      this.redirectToCreateTeam(value as SurveyUseOption);
    }
    return Promise.resolve();
  }

  setRedirectTo(href: string): void {
    if (typeof window !== "undefined") {
      localStorage.setItem(REDIRECT_AFTER_LOGIN_HREF_LS_KEY, href);
    }
  }

  hasPendingRedirect(): boolean {
    return (
      typeof window !== "undefined" &&
      localStorage.getItem(REDIRECT_AFTER_LOGIN_HREF_LS_KEY) !== null
    );
  }

  setRedirectToTeam(permalink: string): void {
    this.setRedirectTo(teamPageUrl({ teamPermalink: permalink, signup: true }));
  }

  forgetRedirectTo(): void {
    if (typeof window !== "undefined") {
      localStorage.removeItem(REDIRECT_AFTER_LOGIN_HREF_LS_KEY);
    }
  }

  async onCreateTeamSubmit(): Promise<void> {
    this.redirectAfterLogin();
    return Promise.resolve();
  }

  async onCreateTeamSkip(): Promise<void> {
    this.redirectAfterLogin();
    return Promise.resolve();
  }

  setPendingAvatar(file: File | null): void {
    if (this.pendingAvatarPreviewUrl) {
      URL.revokeObjectURL(this.pendingAvatarPreviewUrl);
    }
    this.pendingAvatarFile = file;
    this.pendingAvatarPreviewUrl = file ? URL.createObjectURL(file) : null;
  }

  private clearPendingAvatar(): void {
    if (this.pendingAvatarPreviewUrl) {
      URL.revokeObjectURL(this.pendingAvatarPreviewUrl);
    }
    this.pendingAvatarFile = null;
    this.pendingAvatarPreviewUrl = null;
  }

  redirectToNextSignInStep(): void {
    if (this.rootStore.auth.emailClaimed) {
      return;
    }
    if (!this.rootStore.auth.userData?.name) {
      runInAction(() => {
        this.formFields.fullName.value = this.rootStore.auth.fbUser?.displayName ?? "";
      });
      this.redirectToAddFullName();
    } else if (!this.rootStore.auth.userData?.username) {
      this.redirectToAddUsername();
    } else {
      this.redirectAfterLogin();
    }
  }

  private redirectToAddFullName(): void {
    this.rootStore?.router?.push("/add-full-name");
  }

  private redirectToAddUsername(): void {
    this.rootStore?.router?.push("/add-username");
  }

  private redirectToSurveyDescribe(): void {
    this.rootStore?.router?.push("/survey-describe");
  }

  private redirectToSelectSchool(): void {
    this.rootStore?.router?.push("/select-school");
  }

  private redirectToSurveyUse(): void {
    this.rootStore?.router?.push("/survey-use");
  }

  private redirectToCreateTeam(use: SurveyUseOption): void {
    this.rootStore?.router?.push(`/create-team?backTo=/survey-use&use=${use.toString()}`);
  }

  redirectToLogin(noRedirectBack = false): void {
    if (!noRedirectBack) {
      localStorage.setItem(REDIRECT_AFTER_LOGIN_HREF_LS_KEY, window.location.href);
    }
    this.rootStore?.router?.push("/login");
  }

  redirectToSignUp(noRedirectBack = false): void {
    if (!noRedirectBack) {
      localStorage.setItem(REDIRECT_AFTER_LOGIN_HREF_LS_KEY, window.location.href);
    }
    this.rootStore?.router?.push("/signup");
  }

  /** Call when starting a new sign-in flow (e.g. on /login mount) so redirect works again. */
  resetRedirectAfterLoginGuard(): void {
    runInAction(() => {
      this.redirectAfterLoginPromise = null;
    });
  }

  async redirectAfterLogin(): Promise<void> {
    if (this.redirectAfterLoginPromise) {
      return this.redirectAfterLoginPromise;
    }

    this.redirectAfterLoginPromise = this.doRedirectAfterLogin();
    return this.redirectAfterLoginPromise;
  }

  private async doRedirectAfterLogin(): Promise<void> {
    try {
      const router = this.rootStore?.router;
      if (typeof window !== "undefined" && router) {
        const redirectTo = localStorage.getItem(REDIRECT_AFTER_LOGIN_HREF_LS_KEY);
        localStorage.removeItem(REDIRECT_AFTER_LOGIN_HREF_LS_KEY);
        forgetPendingCourse();

        const isDailyChallenge = redirectTo?.includes("/dailychallenge");

        // If redirecting to daily challenge, claim any anonymous attempts
        if (isDailyChallenge) {
          await this.claimDailyChallengeAttempts();
        }

        if (redirectTo) {
          router.push(redirectTo);
        } else {
          // We redirect to the root page because we handle the lastDocId in the root layout.
          // or if user doesn't have a lastDocId, we create a new doc and redirect to it.
          // see: <CreateDocOrRedirect /> component
          router.push("/");
        }

        // Reload with new auth state, but skip for daily challenge routes
        // to avoid triggering middleware that redirects "/" to "/doc"
        if (!isDailyChallenge) {
          router.refresh();
        }
      }
    } finally {
      // Defer reset: router.push() returns void, so we can't await navigation.
      // Delay ensures any concurrent/queued callers see the memoized promise.
      const promiseToClear = this.redirectAfterLoginPromise;
      setTimeout(() => {
        runInAction(() => {
          if (this.redirectAfterLoginPromise === promiseToClear) {
            this.redirectAfterLoginPromise = null;
          }
        });
      }, REDIRECT_GUARD_RESET_MS);
    }
  }

  /**
   * Claim anonymous daily challenge attempts after login/signup.
   * Transfers any attempts made as anonymous user to the authenticated user.
   */
  private async claimDailyChallengeAttempts(): Promise<void> {
    try {
      // Dynamically import to avoid circular dependencies
      const { DailyChallengeApi } = await import("../dailychallenge/utils/api/dailyChallengeApi");

      const anonymousToken = DailyChallengeApi.getAnonymousToken();
      if (!anonymousToken) {
        return;
      }

      const result = await DailyChallengeApi.claimAnonymousAttempts(anonymousToken);

      if (result.migrated_count > 0) {
        // Clear the anonymous token after successful migration
        DailyChallengeApi.clearAnonymousToken();
      }
    } catch (error) {
      // Fail silently - not critical, user can still use the app
      console.error("Failed to claim daily challenge attempts:", error);
    }
  }

  get savedEmail(): string | null {
    if (typeof window !== "undefined") {
      return localStorage.getItem(EMAIL_LS_KEY);
    }
    return null;
  }

  set savedEmail(email: string) {
    if (typeof window !== "undefined") {
      localStorage.setItem(EMAIL_LS_KEY, email);
    }
  }

  resetSavedEmail(): void {
    localStorage.removeItem(EMAIL_LS_KEY);
  }
}
